<script setup lang="ts">
import { computed, type PropType, reactive, ref, watch, watchEffect } from 'vue'
import {
  mapUtil,
  YMap,
  YMapCollection,
  YMapControls,
  YMapDefaultFeaturesLayer,
  YMapDefaultSchemeLayer,
  YMapListener,
  YMapMarker,
} from '@/shared/lib'
import { VMapFull, VMapMarker, VMapSettings, VMapZoom } from '@/shared/ui'
import './assets/style.scss'
import type { ObjectListItemMap } from '@/entities/Object'
import { WObjectMapInfo } from '@/widgets'
import { useRoute } from 'vue-router'
import { useMapSelect, VMapSelect } from '@/shared/ui/VMapSelect'

const props = defineProps({
  objects: {
    type: Array as PropType<ObjectListItemMap[]>,
    default: () => [],
  },
})

/* Обработка вывода информации об объектах */
const cabinets = ref<ObjectListItemMap[]>([])
const isShowCabinets = ref(true)

const coordsSum = reactive<{ longitude: number[]; latitude: number[] }>({
  longitude: [],
  latitude: [],
})

watchEffect(() => {
  props.objects.forEach((item) => {
    coordsSum.latitude.push(item.latitude)
    coordsSum.longitude.push(item.longitude)

    switch (item.object_type_id) {
      default:
        cabinets.value.push(item)
    }
  })
})

/* Работа с размещением объектов на карте */
const map = ref()
const full = ref<boolean>(false)
const zoom = ref<number>(16)
const center = ref<number[]>([37.659565156528, 55.711520791225])

/**
 * Вычисляем центральное положение на карте
 * когда есть несколько элементов на карте
 */
watchEffect(() => {
  if (!props.objects.length) return

  if (props.objects.length == 1) {
    center.value = [props.objects[0].longitude, props.objects[0].latitude]
  } else {
    map.value.entity.setLocation({
      bounds: mapUtil.getBounds(coordsSum.longitude, coordsSum.latitude),
    })

    zoom.value = map.value.entity.zoom
  }
})

/* Список выделенных объектов */
const selectedObject = ref<ObjectListItemMap[]>([])

/* Вспомогательные функции выделения */
const { startSelectHandler, endSelectHandler, selectObjectToClick, selectObjectToSelected } =
  useMapSelect()

/**
 * Обработка клика по объекту карты
 * @param object
 * @param event
 */
const markerClickHandler = (object: ObjectListItemMap, event: any) => {
  event.stopPropagation()

  selectObjectToClick(object, selectedObject, event)
}

/**
 * Функция выделения объектов на карте
 * @param coords
 */
const selectedHandler = (coords: number[][]) => {
  selectObjectToSelected(selectedObject, props.objects, coords)
}

/**
 * Обработка клика по карте вне объектов
 */
const clickMapHandler = () => {
  selectedObject.value = []
}

/**
 * Получаем ссылку для установки координат при
 * отсутствии объектов на карте
 */
const route = useRoute()

const setCoordsLink = computed(() => {
  const params = route.params
  return `/objects/${params.type}/${params.object_id}/admin`
})

/* Снимаем выделение при изменении роута */
watch(
  () => route.params,
  () => {
    selectedObject.value = []
  },
)
</script>

<template>
  <YMap
    class="w-object-map__wrapper"
    :location="{ center, zoom }"
    :class="full ? 'position_page-full' : ''"
    ref="map"
  >
    <YMapDefaultSchemeLayer />
    <YMapDefaultFeaturesLayer />

    <template v-if="objects.length">
      <YMapCollection v-if="isShowCabinets">
        <template v-for="cabinet in cabinets" :key="cabinet.id">
          <YMapMarker
            :coordinates="[cabinet.longitude, cabinet.latitude]"
            @click="(_, event: any) => markerClickHandler(cabinet, event)"
          >
            <VMapMarker
              type="host"
              :select="selectedObject.some((item) => item.id === cabinet.id)"
              :color="cabinet.is_connected ? 'teal-light' : 'red-dark'"
            />
          </YMapMarker>
        </template>
      </YMapCollection>
    </template>

    <div class="w-object-map__empty" v-else>
      <RouterLink :to="setCoordsLink">
        <VBtn>{{ $t('itf.set_coordinates') }}</VBtn>
      </RouterLink>
    </div>

    <YMapControls position="top left">
      <VMapSettings>
        <div class="mb-1">{{ $t('itf.object_type') }}</div>

        <VCheckbox v-model="isShowCabinets" hide-details label="ШУНО" />
      </VMapSettings>

      <VMapZoom v-model="zoom" />

      <VMapFull v-model="full" />

      <WObjectMapInfo :items="selectedObject" />

      <VMapSelect @selected="selectedHandler" />
    </YMapControls>

    <YMapListener
      @click="clickMapHandler"
      @mouseDown="startSelectHandler"
      @mouseUp="endSelectHandler"
    />
  </YMap>
</template>
